import { Card, Col, OverlayTrigger, Row, Tooltip, Modal, Button, Badge, Form } from "react-bootstrap";
import { useQuery } from "react-query";
import { DEV_SERVER } from "../../../constants/urls";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import axios from "axios";
import { userSignal } from "../../../signal/userSignal";
import * as moment from "moment";
import DataTable from "react-data-table-component";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";

export default function FarmerFieldVisits() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [farmerFields, setFarmerFields] = useState([]);
    const [farmerId, setFarmerId] = useState(searchParams.get("farmerId") ? searchParams.get("farmerId") : "");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(35);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [selectedField, setSelectedField] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [farmersList, setFarmersList] = useState([]);

    const getFarmerFields = useQuery(
        ["FarmerFieldVisits", page, limit, farmerId, search],
        async ({ signal }) => {
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('limit', limit);
            if (farmerId) params.append('farmerId', farmerId);
            if (search) params.append('search', search);
            
            let resp = await axios.get(`${DEV_SERVER}/farmerfield/v2?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${userSignal.value?.token}`,
                },
                signal: signal
            });
            return resp.data;
        },
        {
            enabled: false,
            onSuccess: (data) => {
                if (data.status) {
                    setFarmerFields(data.data?.data || []);
                    setTotalItems(data.data?.pagination?.total || 0);
                    setTotalPages(data.data?.pagination?.pages || 0);
                    setPage(data.data?.pagination?.page || 1);
                } else {
                    toast.error(data.message);
                }
            },
            onError: (error) => {
                if (error.response?.data?.message) {
                    toast.error(error.response.data.message);
                } else {
                    toast.error("Something went wrong");
                }
            }
        }
    );

    const getFarmers = useQuery("getFarmersForFieldVisits", async () => {
        let resp = await axios.get(`${DEV_SERVER}/farmer/v2`, {
            headers: {
                Authorization: `Bearer ${userSignal.value?.token}`,
            },
        });
        return resp.data;
    }, {
        enabled: false,
        onSuccess: (data) => {
            if (data.status) {
                const farmers = data.data?.farmers || data.data || [];
                setFarmersList(farmers.map(f => ({ label: f.name, value: f._id })));
            }
        },
    });

    useEffect(() => {
        getFarmerFields.refetch({ cancelRefetch: true });
        getFarmers.refetch({ cancelRefetch: true });
    }, []);

    useEffect(() => {
        if (page > 0) {
            getFarmerFields.refetch({ cancelRefetch: true });
        }
    }, [page, limit]);

    const handleFilters = () => {
        let tempSearch = new URLSearchParams();
        if (farmerId) tempSearch.append("farmerId", farmerId);
        setSearchParams(tempSearch);
        setPage(1);
        getFarmerFields.refetch({ cancelRefetch: true });
    };

    const handleReset = () => {
        setFarmerId("");
        setSearch("");
        setSearchParams({});
        setPage(1);
        getFarmerFields.refetch({ cancelRefetch: true });
    };

    // Custom styles for DataTable
    const customStyles = {
        table: {
            style: {
                width: '100%',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                minHeight: '52px',
            },
        },
        headCells: {
            style: {
                fontSize: '14px',
                fontWeight: '600',
                color: '#495057',
                paddingLeft: '12px',
                paddingRight: '12px',
                justifyContent: 'center',
            },
        },
        rows: {
            style: {
                minHeight: '60px',
                '&:not(:last-of-type)': {
                    borderBottom: '1px solid #dee2e6',
                },
                '&:hover': {
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer',
                },
            },
        },
        cells: {
            style: {
                paddingLeft: '12px',
                paddingRight: '12px',
                fontSize: '14px',
                justifyContent: 'center',
            },
        },
        pagination: {
            style: {
                borderTop: '1px solid #dee2e6',
                minHeight: '56px',
            },
        },
    };

    const columns = [
        {
            name: "Village",
            selector: (row) => row?.Village || "N/A",
            sortable: true,
            center: true,
            grow: 1,
            style: {
                fontSize: '14px',
            }
        },
        {
            name: "Farmer",
            selector: (row) => row?.FarmerName || row?.farmerId?.name || "N/A",
            sortable: true,
            center: true,
            grow: 1.5,
            cell: (row) => {
                const name = row?.FarmerName || row?.farmerId?.name || "N/A";
                return (
                    <OverlayTrigger placement="top" overlay={<Tooltip style={{ position: "fixed" }}>{name}</Tooltip>}>
                        <div style={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%',
                            fontSize: '14px',
                            textAlign: 'center'
                        }}>
                            {name}
                        </div>
                    </OverlayTrigger>
                );
            }
        },
        {
            name: "Crop Name",
            selector: (row) => row?.CropName || "N/A",
            sortable: true,
            center: true,
            grow: 1,
            style: {
                fontSize: '14px',
            }
        },
        {
            name: "Land (Acres)",
            selector: (row) => row?.LandHoldingAcres || 0,
            sortable: true,
            center: true,
            grow: 1,
            style: {
                fontSize: '14px',
            }
        },
        {
            name: "District",
            selector: (row) => row?.District || "N/A",
            sortable: true,
            center: true,
            grow: 1,
            style: {
                fontSize: '14px',
            }
        },
        {
            name: "Contact",
            selector: (row) => row?.ContactNo || "N/A",
            center: true,
            grow: 1.2,
            style: {
                fontSize: '14px',
            }
        },
        {
            name: "User",
            selector: (row) => row?.userId?.name || "N/A",
            center: true,
            grow: 1,
            cell: (row) => {
                const name = row?.userId?.name || "N/A";
                return (
                    <OverlayTrigger placement="top" overlay={<Tooltip style={{ position: "fixed" }}>{name}</Tooltip>}>
                        <div style={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            width: '100%',
                            fontSize: '14px',
                            textAlign: 'center'
                        }}>
                            {name}
                        </div>
                    </OverlayTrigger>
                );
            }
        },
        {
            name: "Created At",
            selector: (row) => row?.createdAt ? moment(parseInt(row.createdAt)).format("DD/MM/YY hh:mm A") : "N/A",
            sortable: true,
            center: true,
            grow: 1.5,
            style: {
                fontSize: '14px',
            }
        },
        {
            name: "Actions",
            center: true,
            grow: 1.2,
            cell: (row) => (
                <Button
                    size="sm"
                    variant="primary"
                    onClick={() => {
                        setSelectedField(row);
                        setShowDetailsModal(true);
                    }}
                    style={{
                        padding: '6px 16px',
                        fontSize: '13px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap'
                    }}
                >
                    View Details
                </Button>
            ),
        },
    ];

    return (
        <>
            <Row>
                <Col sm="12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <div className="header-title">
                                <h4 className="card-title">Farmer Field Visits</h4>
                            </div>
                        </Card.Header>
                         <Card.Body>
                            {/* Filters */}
                            <Row className="mb-3 align-items-end">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label>Farmer</Form.Label>
                                        <Select
                                            options={farmersList}
                                            value={farmersList.find(f => f.value === farmerId) || null}
                                            onChange={(selected) => setFarmerId(selected?.value || "")}
                                            isClearable
                                            placeholder="Select Farmer"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Search</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search by village, crop, district..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={5} className="d-flex align-items-end gap-2">
                                    <Button
                                        variant="primary"
                                        onClick={handleFilters}
                                        style={{
                                            minWidth: '100px',
                                            height: '38px',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Apply
                                    </Button>
                                    <Button
                                        variant="secondary"
                                        onClick={handleReset}
                                        style={{
                                            minWidth: '100px',
                                            height: '38px',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Reset
                                    </Button>
                                </Col>
                            </Row>

                            {/* Data Table */}
                            {(getFarmerFields.isLoading || getFarmerFields.isFetching) ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                                    <div className="spinner-border text-primary m-2" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <DataTable
                                    columns={columns}
                                    data={farmerFields}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalItems}
                                    paginationPerPage={limit}
                                    paginationRowsPerPageOptions={[10, 20, 35, 50, 100]}
                                    paginationDefaultPage={page}
                                    onChangeRowsPerPage={(currentRowsPerPage) => {
                                        setLimit(currentRowsPerPage);
                                        setPage(1);
                                    }}
                                    onChangePage={(currentPage) => {
                                        setPage(currentPage);
                                    }}
                                    highlightOnHover
                                    responsive
                                    customStyles={customStyles}
                                    noDataComponent={
                                        <div className="p-4 text-center text-muted">
                                            No records found
                                        </div>
                                    }
                                />
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Farmer Field Details Modal - Keep the same as before */}
           <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="xl">
                <Modal.Header closeButton style={{ backgroundColor: '#fff', color: 'white', borderBottom: 'none' }}>
                    <Modal.Title>Farmer Field Details</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                    {selectedField && (
                        <Row>
                            {/* Field Visit Information */}
                            <Col md={6}>
                                <Card className="mb-3 shadow-sm border-0">
                                    <Card.Header style={{ backgroundColor: '#fff', color: 'white', padding: '12px 20px' }}>
                                        <h6 className="mb-0 d-flex align-items-center">
                                            <i className="fas fa-map-marker-alt me-2"></i>
                                            Field Visit Information
                                        </h6>
                                    </Card.Header>
                                    <Card.Body style={{ backgroundColor: 'white' }}>
                                        <table className="table table-sm table-borderless mb-0">
                                            <tbody>
                                                {selectedField.userId?.name && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{width: '45%', paddingTop: '8px', paddingBottom: '8px'}}>Created By:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.userId.name}</td>
                                                    </tr>
                                                )}
                                                {(selectedField.createdAt || selectedField.FieldVisit) && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{width: '45%', paddingTop: '8px', paddingBottom: '8px'}}>Field Visit Date:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.createdAt ? moment(parseInt(selectedField.createdAt)).format("DD/MM/YYYY hh:mm A") : (selectedField.FieldVisit ? moment(parseInt(selectedField.FieldVisit)).format("DD/MM/YYYY hh:mm A") : "N/A")}</td>
                                                    </tr>
                                                )}
                                                {selectedField.FarmerName && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Farmer Name:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.FarmerName}</td>
                                                    </tr>
                                                )}
                                                {selectedField.Village && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Village:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.Village}</td>
                                                    </tr>
                                                )}
                                                {selectedField.Tehsil && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Tehsil:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.Tehsil}</td>
                                                    </tr>
                                                )}
                                                {selectedField.District && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>District:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.District}</td>
                                                    </tr>
                                                )}
                                                {selectedField.Pincode && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Pincode:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.Pincode}</td>
                                                    </tr>
                                                )}
                                                {selectedField.ContactNo && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Contact No:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.ContactNo}</td>
                                                    </tr>
                                                )}
                                                {selectedField.LandHoldingAcres !== undefined && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Land Holding:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.LandHoldingAcres} Acres</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Crop Information */}
                            <Col md={6}>
                                <Card className="mb-3 shadow-sm border-0">
                                    <Card.Header style={{ backgroundColor: '#fff', color: 'white', padding: '12px 20px' }}>
                                        <h6 className="mb-0 d-flex align-items-center">
                                            <i className="fas fa-seedling me-2"></i>
                                            Crop Information
                                        </h6>
                                    </Card.Header>
                                    <Card.Body style={{ backgroundColor: 'white' }}>
                                        <table className="table table-sm table-borderless mb-0">
                                            <tbody>
                                                {selectedField.CropName && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{width: '45%', paddingTop: '8px', paddingBottom: '8px'}}>Crop Name:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.CropName}</td>
                                                    </tr>
                                                )}
                                                {selectedField.NumberofAcresSowing !== undefined && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Acres Sowing:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.NumberofAcresSowing} Acres</td>
                                                    </tr>
                                                )}
                                                {selectedField.VarietyAndCompany && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Variety & Company:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.VarietyAndCompany}</td>
                                                    </tr>
                                                )}
                                                {selectedField.DaysofCrop !== undefined && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Days of Crop:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.DaysofCrop} Days</td>
                                                    </tr>
                                                )}
                                                {selectedField.DateOfSowing && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Date of Sowing:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{moment(parseInt(selectedField.DateOfSowing)).format("DD/MM/YYYY")}</td>
                                                    </tr>
                                                )}
                                                {selectedField.DeficiencyOnCrop && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Deficiency on Crop:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.DeficiencyOnCrop}</td>
                                                    </tr>
                                                )}
                                                {selectedField.SuggestionbyYou && (
                                                    <tr>
                                                        <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Suggestions:</td>
                                                        <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.SuggestionbyYou}</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Demonstration Information */}
                            {(selectedField.Demonstration || selectedField.Products) && (
                                <Col md={6}>
                                    <Card className="mb-3 shadow-sm border-0">
                                        <Card.Header style={{ backgroundColor: '#fff', color: 'white', padding: '12px 20px' }}>
                                            <h6 className="mb-0 d-flex align-items-center">
                                                <i className="fas fa-flask me-2"></i>
                                                Demonstration Information
                                            </h6>
                                        </Card.Header>
                                        <Card.Body style={{ backgroundColor: 'white' }}>
                                            <table className="table table-sm table-borderless mb-0">
                                                <tbody>
                                                    {selectedField.Demonstration && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{width: '45%', paddingTop: '8px', paddingBottom: '8px'}}>Demonstration:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.Demonstration}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.Products && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Products:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.Products}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.DoseofProducts && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Dose of Products:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.DoseofProducts}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.SelectedAreaForDemo && (
                                                        <>
                                                            {selectedField.SelectedAreaForDemo.NoOfPlants !== undefined && (
                                                                <tr>
                                                                    <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>No. of Plants:</td>
                                                                    <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.SelectedAreaForDemo.NoOfPlants}</td>
                                                                </tr>
                                                            )}
                                                            {selectedField.SelectedAreaForDemo.NoOfRows !== undefined && (
                                                                <tr>
                                                                    <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>No. of Rows:</td>
                                                                    <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.SelectedAreaForDemo.NoOfRows}</td>
                                                                </tr>
                                                            )}
                                                            {selectedField.SelectedAreaForDemo.NoOfAcres !== undefined && (
                                                                <tr>
                                                                    <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>No. of Acres:</td>
                                                                    <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.SelectedAreaForDemo.NoOfAcres}</td>
                                                                </tr>
                                                            )}
                                                        </>
                                                    )}
                                                    {selectedField.ObservationOnPlot && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Observation on Plot:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.ObservationOnPlot}</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}

                            {/* Field Day Information */}
                            {(selectedField.FieldDay || selectedField.Crop) && (
                                <Col md={6}>
                                    <Card className="mb-3 shadow-sm border-0">
                                        <Card.Header style={{ backgroundColor: '#fff', color: 'white', padding: '12px 20px' }}>
                                            <h6 className="mb-0 d-flex align-items-center">
                                                <i className="fas fa-calendar-day me-2"></i>
                                                Field Day Information
                                            </h6>
                                        </Card.Header>
                                        <Card.Body style={{ backgroundColor: 'white' }}>
                                            <table className="table table-sm table-borderless mb-0">
                                                <tbody>
                                                    {selectedField.FieldDay && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{width: '45%', paddingTop: '8px', paddingBottom: '8px'}}>Field Day:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.FieldDay}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.Crop && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Crop:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.Crop}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.ProductsUsed && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Products Used:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.ProductsUsed}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.ProductPurchasedFrom && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Product Purchased From:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.ProductPurchasedFrom}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.FarmersAttendance !== undefined && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Farmers Attendance:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.FarmersAttendance}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.ConductedById?.name && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Conducted By:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.ConductedById.name}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.FeedbackOnFieldDay && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Feedback:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.FeedbackOnFieldDay}</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}

                            {/* Liquidation Information */}
                            {(selectedField.Liquidation || selectedField.Product) && (
                                <Col md={6}>
                                    <Card className="mb-3 shadow-sm border-0">
                                        <Card.Header style={{ backgroundColor: '#fff', color: 'white', padding: '12px 20px' }}>
                                            <h6 className="mb-0 d-flex align-items-center">
                                                <i className="fas fa-money-bill-wave me-2"></i>
                                                Liquidation Information
                                            </h6>
                                        </Card.Header>
                                        <Card.Body style={{ backgroundColor: 'white' }}>
                                            <table className="table table-sm table-borderless mb-0">
                                                <tbody>
                                                    {selectedField.Liquidation && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{width: '45%', paddingTop: '8px', paddingBottom: '8px'}}>Liquidation:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.Liquidation}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.FarmerDetails && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Farmer Details:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.FarmerDetails}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.Product && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Product:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.Product}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.Quantity !== undefined && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Quantity:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.Quantity}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.DealerNameById?.name && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Dealer:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.DealerNameById.name}</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}

                            {/* Farmers Meeting Information */}
                            {(selectedField.FarmersMeeting || selectedField.PlaceOfMeeting) && (
                                <Col md={6}>
                                    <Card className="mb-3 shadow-sm border-0">
                                        <Card.Header style={{ backgroundColor: '#fff', color: 'white', padding: '12px 20px' }}>
                                            <h6 className="mb-0 d-flex align-items-center">
                                                <i className="fas fa-users me-2"></i>
                                                Farmers Meeting Information
                                            </h6>
                                        </Card.Header>
                                        <Card.Body style={{ backgroundColor: 'white' }}>
                                            <table className="table table-sm table-borderless mb-0">
                                                <tbody>
                                                    {selectedField.FarmersMeeting && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{width: '45%', paddingTop: '8px', paddingBottom: '8px'}}>Farmers Meeting:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.FarmersMeeting}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.PlaceOfMeeting && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Place of Meeting:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.PlaceOfMeeting}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.FocusedCrops && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Focused Crops:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.FocusedCrops}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.CoveredVillages && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Covered Villages:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.CoveredVillages}</td>
                                                        </tr>
                                                    )}
                                                    {selectedField.MeetingConductedById?.name && (
                                                        <tr>
                                                            <td className="fw-bold text-muted" style={{paddingTop: '8px', paddingBottom: '8px'}}>Meeting Conducted By:</td>
                                                            <td style={{paddingTop: '8px', paddingBottom: '8px'}}>{selectedField.MeetingConductedById.name}</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            )}
                        </Row>
                    )}
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowDetailsModal(false)}
                        style={{ minWidth: '100px' }}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
